/**
 * Dividend API service.
 *
 * All calls go through {@link apiClient} with paths that omit the `/api`
 * prefix (the base URL already includes `/api` via runtime config), matching
 * `protective-put-service.ts`.
 *
 * Validates: Requirements 1.1, 5.1, 6.1, 7.1
 */

import type {
  DividendInput,
  DividendRecord,
  DividendUpdateInput,
} from '@covered-calls/shared';
import { apiClient } from './api-client';

/**
 * Record a cash dividend received on a position's underlying stock.
 */
export async function createDividend(
  positionId: string,
  input: DividendInput
): Promise<DividendRecord> {
  return apiClient.post<DividendRecord>(`/trades/${positionId}/dividends`, input);
}

/**
 * Fetch the recorded dividends for a position.
 */
export async function getDividends(positionId: string): Promise<DividendRecord[]> {
  return apiClient.get<DividendRecord[]>(`/trades/${positionId}/dividends`);
}

/**
 * Update an existing dividend record, guarded by optimistic locking.
 */
export async function updateDividend(
  positionId: string,
  dividendId: string,
  input: DividendUpdateInput
): Promise<DividendRecord> {
  return apiClient.put<DividendRecord>(
    `/trades/${positionId}/dividends/${dividendId}`,
    input
  );
}

/**
 * Delete a dividend record from a position.
 */
export async function deleteDividend(
  positionId: string,
  dividendId: string
): Promise<void> {
  await apiClient.delete(`/trades/${positionId}/dividends/${dividendId}`);
}
